import React from 'react';
import { MovieProjectDetail, MovieScene } from '../../services/api/movieService';
import { Loader2, AlertCircle, Download, Combine, CheckCircle2, Film } from 'lucide-react';

/**
 * ステップ④: 結合・書き出し。
 *
 * シーン順のタイムラインプレビューを表示し、全クリップが揃ったら
 * ffmpeg による結合ジョブを発行する。完成動画はプレビュー + ダウンロード可能。
 * クリップを再生成した場合は再結合できる。
 */

interface ConcatStepProps {
  project: MovieProjectDetail;
  scenes: MovieScene[];
  isRequesting: boolean;
  onConcat: () => void;
}

const ConcatStep: React.FC<ConcatStepProps> = ({ project, scenes, isRequesting, onConcat }) => {
  if (scenes.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-300 rounded-xl">
        先に「① 台本・構成」でシーンを作成してください
      </div>
    );
  }

  const missingClips = scenes.filter((s) => !s.clip.video);
  const allReady = missingClips.length === 0;
  const isConcatenating = project.status === 'concatenating';
  const totalDuration = scenes.reduce((sum, s) => sum + s.duration_sec, 0);

  return (
    <div className="space-y-6">
      {/* タイムラインプレビュー */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          タイムライン
          <span className="ml-2 text-xs font-normal text-gray-500">
            {scenes.length}シーン / 合計約{totalDuration}秒
          </span>
        </h3>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {scenes.map((scene, index) => (
            <div key={scene.id} className="shrink-0 w-36">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative flex items-center justify-center border border-warm-border">
                {scene.clip.video ? (
                  <video src={scene.clip.video.url} preload="metadata" muted className="w-full h-full object-cover" />
                ) : scene.storyboard.image ? (
                  <>
                    <img src={scene.storyboard.image.url} alt="" className="w-full h-full object-cover opacity-40" />
                    <AlertCircle className="w-5 h-5 text-amber-500 absolute" />
                  </>
                ) : (
                  <Film className="w-6 h-6 text-gray-300" />
                )}
              </div>
              <div className="flex items-center justify-between mt-1 px-0.5">
                <span className="text-[11px] text-gray-500">
                  {index + 1}. {scene.duration_sec}秒
                </span>
                {scene.clip.video ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <span className="text-[11px] text-amber-600">未生成</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 結合アクション */}
      <div className="bg-gray-50 rounded-xl p-4 border border-warm-border">
        {!allReady && (
          <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            クリップ未生成のシーンが{missingClips.length}件あります。「③ クリップ」で全シーンを生成すると結合できます。
          </div>
        )}

        {project.status === 'concat_failed' && project.concat_error_message && (
          <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            結合に失敗しました: {project.concat_error_message}
          </div>
        )}

        <button
          onClick={onConcat}
          disabled={!allReady || isConcatenating || isRequesting}
          className="flex items-center gap-2 px-5 py-2.5 text-sm bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
        >
          {isConcatenating || isRequesting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Combine className="w-4 h-4" />
          )}
          {isConcatenating
            ? '結合処理中...（完了すると自動で表示されます）'
            : project.final_video_url
              ? '再結合する'
              : '全シーンを結合する'}
        </button>
      </div>

      {/* 完成動画 */}
      {project.final_video_url && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            完成動画
            {project.concatenated_at && (
              <span className="text-xs font-normal text-gray-400">
                （{new Date(project.concatenated_at).toLocaleString('ja-JP')} 作成）
              </span>
            )}
          </h3>
          <div className="max-w-2xl">
            <video
              src={project.final_video_url}
              controls
              className="w-full rounded-xl border border-warm-border bg-black"
            />
            <a
              href={project.final_video_url}
              download={`${project.title}.mp4`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
            >
              <Download className="w-4 h-4" />
              ダウンロード
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConcatStep;
